import { mapActions } from 'vuex';

//---helpers
import { globalLoader, pluralizate } from '@/helpers/variables';

//---mixins
import { dynamicHead } from '@/mixins/dynamic-head.js';

//---components
import BreadcrumbsSimple from '@/components/@general/BreadcrumbsSimple/index.vue';
import Pagination from '@/components/@general/Pagination/index.vue';
import PageNotFound from '@/modules/System/PageNotFound/index.vue';
import AuthorHero from './components/AuthorHero/index.vue';
import AuthorSection from './components/AuthorSection/index.vue';
import AuthorArticleCard from './components/AuthorArticleCard/index.vue';

import chevronLeftIcon from '@/assets/img/author/chevron-left.svg';
import chevronRightIcon from '@/assets/img/author/chevron-right.svg';

const TOPICS_AUTOPLAY_INTERVAL = 4000;
const TOPICS_SLIDE_DURATION = 900;

const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

export default {
  name: 'Author',
  mixins: [dynamicHead],
  components: { BreadcrumbsSimple, Pagination, PageNotFound, AuthorHero, AuthorSection, AuthorArticleCard },
  data() {
    return {
      chevronLeftIcon,
      chevronRightIcon,
      author: null,
      articles: [],
      articlesMeta: null,
      activeTopic: 0,
      topicsTimer: null,
      topicsFrame: null,
      isLoadingMore: false,
      isNotFound: false
    };
  },
  created() {
    this._loadContent();
  },
  beforeDestroy() {
    this.stopTopicsAutoplay();
  },
  watch: {
    '$route.params.page'() {
      this._loadArticles().then(() => this.scrollToArticles());
    }
  },
  computed: {
    breadcrumbs() {
      return [{ title: this.$t('page.interesting.title'), route: { name: 'interesting' } }];
    },
    sectionNumbers() {
      let { biographyLead, biography, topics, achievements } = this.author || {};
      let sections = {
        biography: !!(biographyLead || biography),
        topics: !!topics?.length,
        achievements: !!achievements?.length,
        articles: true
      };
      let counter = 0;
      return Object.keys(sections).reduce((acc, key) => {
        acc[key] = sections[key] ? ++counter : 0;
        return acc;
      }, {});
    },
    hasMoreArticles() {
      return !!this.articlesMeta && this.articlesMeta.currentPage < this.articlesMeta.lastPage;
    },
    articlesTotal() {
      return this.articlesMeta?.total || 0;
    },
    materialsPluralizate() {
      return pluralizate(
        this.articlesTotal,
        this.$t('page.author.materialsOne'),
        this.$t('page.author.materialsSimple'),
        this.$t('page.author.materialsMany')
      );
    }
  },
  methods: {
    ...mapActions({
      getAuthor: 'blog/GET_AUTHOR',
      getAuthorArticles: 'blog/GET_AUTHOR_ARTICLES'
    }),
    formatNumber(value) {
      return String(value).padStart(2, '0');
    },
    startTopicsAutoplay() {
      this.stopTopicsAutoplay();
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.topicsTimer = setInterval(this.nextTopic, TOPICS_AUTOPLAY_INTERVAL);
    },
    stopTopicsAutoplay() {
      clearInterval(this.topicsTimer);
      this.topicsTimer = null;
      this.stopTopicsSlide();
    },
    stopTopicsSlide() {
      cancelAnimationFrame(this.topicsFrame);
      this.topicsFrame = null;
      if (this.$refs.topics) this.$refs.topics.style.scrollSnapType = '';
    },
    slideTopicsTo(left) {
      let list = this.$refs.topics;
      let from = list.scrollLeft;
      let to = Math.min(left, list.scrollWidth - list.clientWidth);
      let start = null;
      this.stopTopicsSlide();
      list.style.scrollSnapType = 'none';
      let step = (time) => {
        if (start === null) start = time;
        let progress = Math.min((time - start) / TOPICS_SLIDE_DURATION, 1);
        list.scrollLeft = from + (to - from) * easeInOutCubic(progress);
        if (progress < 1) {
          this.topicsFrame = requestAnimationFrame(step);
        } else {
          this.stopTopicsSlide();
        }
      };
      this.topicsFrame = requestAnimationFrame(step);
    },
    nextTopic() {
      if (document.hidden) return;
      let isLast = this.activeTopic >= this.author.topics.length - 1;
      this._slideToTopic(isLast ? 0 : this.activeTopic + 1);
    },
    goToTopic(index) {
      this.stopTopicsAutoplay();
      this._slideToTopic(index);
    },
    _slideToTopic(index) {
      let list = this.$refs.topics;
      let card = list?.firstElementChild;
      if (!card || list.scrollWidth <= list.clientWidth) return;
      let step = card.offsetWidth + parseFloat(getComputedStyle(list).columnGap || 0);
      this.slideTopicsTo(index * step);
    },
    onTopicsScroll() {
      let list = this.$refs.topics;
      let card = list?.firstElementChild;
      if (!card) return;
      let isEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - 1;
      let step = card.offsetWidth + parseFloat(getComputedStyle(list).columnGap || 0);
      let lastIndex = this.author.topics.length - 1;
      this.activeTopic = isEnd ? lastIndex : Math.min(Math.round(list.scrollLeft / step), lastIndex);
    },
    async loadMoreArticles() {
      this.isLoadingMore = true;
      try {
        let result = await this.getAuthorArticles({
          slug: this.$route.params.slug,
          page: this.articlesMeta.currentPage + 1
        });
        this.articles = [...this.articles, ...result.data];
        this.articlesMeta = result.meta;
      } finally {
        this.isLoadingMore = false;
      }
    },
    scrollToArticles() {
      this.$refs.articles?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    async _loadContent() {
      globalLoader(true);
      try {
        let [author] = await Promise.all([this.getAuthor(this.$route.params.slug), this._loadArticles()]);
        this.author = this._normalizeAuthor(author.data);
        this._setMetaParams();
        this.$nextTick(() => {
          this.startTopicsAutoplay();
          if (this.$route.hash === '#articles') this.scrollToArticles();
        });
      } catch (e) {
        this.isNotFound = true;
      }
      globalLoader(false);
    },
    _normalizeAuthor(author) {
      return {
        ...author,
        stats: (author.stats || []).filter(({ value, label }) => value && label),
        topics: (author.topics || [])
          .filter(({ title }) => title)
          .map((topic) => ({ ...topic, tags: (topic.tags || []).filter(Boolean) })),
        achievements: (author.achievements || []).filter(({ title }) => title),
        links: (author.links || []).filter(({ url }) => url)
      };
    },
    async _loadArticles() {
      let { slug, page } = this.$route.params;
      let result = await this.getAuthorArticles({ slug, page: page ? Number(page) : 1 });
      this.articles = result.data;
      this.articlesMeta = result.meta;
    },
    _authorUrl() {
      let location = this.$localize({ name: 'author', params: { slug: this.author.slug } });
      return this._canonicalUrl(this.$router.resolve(location).route.path);
    },
    _setMetaParams() {
      let { name, metaTitle, metaDescription, shortBio, position, image, links } = this.author;
      this.metaParams.title = `${metaTitle || name}${this.$t('seo.metaTitleGeneral')}`;
      this.metaParams.description = metaDescription || shortBio;
      this.metaParams.image = image ? new URL(image, window.location.origin).href : null;
      this.setMeta();
      this.setJsonLdSchema('Person', {
        '@type': 'Person',
        name,
        jobTitle: position,
        description: shortBio,
        image: this.metaParams.image,
        url: this._authorUrl(),
        sameAs: (links || []).map(({ url }) => url)
      });
    }
  }
};
